import { useState, useEffect, useContext } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FaFileExport, FaSignOutAlt, FaTimes, FaPlus, FaCheck, FaEye, FaMicrophone } from "react-icons/fa";
import classes from "./DischargeModal.module.css";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";


// ✅ Print and Export components for discharge
import Print, { exportDischargePDF } from "./DPrint";

import AuthContext from "../../../../store/auth-context";
import { calculateAgeDetailed } from "../../../../util/ageCalculator";

const DischargeModal = ({ show, onClose, onSubmit, patient, hospitalData, loading }) => {
  const { t } = useTranslation();
  const authCtx = useContext(AuthContext);
  const [form, setForm] = useState({
    patientName: "",
    age: "",
    sex: "",
    weight: "",
    doa: "",
    doaTime: "",
    dod: "",
    dodTime: "",
    ipdNo: "",
    address: "",
    diagnosis: "",
    dischargeType: "",
    condition: "",
    finalDiagnosis: "",
    summaryNotes: "",
    notes: "",
    paymentClearance: "",
    nextAppointment: "",
    drWrittenDischargeSummery: "", // Property name as requested
    pho: "",
    co: "",
    kco: "",
    cf: "",
  });

  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [tempSummary, setTempSummary] = useState("");
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isListeningNotes, setIsListeningNotes] = useState(false);

  const [isSubmitted, setIsSubmitted] = useState(false);

  // ✅ Pre-fill form when patient data is available or modal opens
  useEffect(() => {
    if (show && patient) {
      const calculateAge = (dob) => {
        const { years, text } = calculateAgeDetailed(dob);
        return text || years;
      };

      const mapGender = (g) => {
        if (!g) return "";
        const lowerG = String(g).toLowerCase();
        if (lowerG === "m" || lowerG === "male") return "Male";
        if (lowerG === "f" || lowerG === "female") return "Female";
        return g.charAt(0).toUpperCase() + g.slice(1).toLowerCase(); // Default capitalization
      };

      setForm((prev) => ({
        ...prev,
        patientName: patient.name || "",
        age: calculateAge(patient.dob) || patient.age || "",
        sex: mapGender(patient.gender) || "",
        weight: patient.vitals?.weight?.replace(" kg", "") || "",
        doa: patient.admissionDate || "",
        doaTime: patient.admissionTime || "",
        ipdNo: patient.ipdAdmissionId || "",
        address: patient.address || "",
        diagnosis: patient.diagnosis || patient.admissionReason || "",
        notes: patient.notes || "",
        // If already discharged, use the saved date and time, otherwise default to "now"
        dod: patient.status === "DISCHARGED" && patient.dischargeDateTime
          ? patient.dischargeDateTime.split(/[ T]/)[0]
          : new Date().toISOString().split("T")[0],
        dodTime: patient.status === "DISCHARGED" && patient.dischargeDateTime
          ? (patient.dischargeDateTime.split(/[ T]/)[1] || "").slice(0, 5)
          : new Date().toTimeString().slice(0, 5),
        dischargeType: patient.status === "DISCHARGED" ? patient.dischargeType || "" : "",
        condition: patient.status === "DISCHARGED" ? patient.conditionAtDischarge || "" : "",
        paymentClearance: patient.status === "DISCHARGED" ? patient.paymentClearance || "" : "",
        drWrittenDischargeSummery: patient.status === "DISCHARGED" ? patient.drWrittenDischargeSummery || "" : "",
        pho: patient.pho || "",
        co: patient.co || "",
        kco: patient.kco || "",
        cf: patient.cf || "",
        nextAppointment: patient.status === "DISCHARGED" ? patient.nextAppointment || patient.next_appointment || "" : ""
      }));
      setIsSubmitted(false);
    } else if (!show) {
      setIsSubmitted(false);
    }
  }, [show, patient]);


  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = () => {
    const errors = [];
    setIsSubmitted(true);
    if (!form.dod) errors.push(t("Discharge date (DOD) is required"));
    if (!form.dodTime) errors.push(t("Discharge time is required"));
    if (!form.dischargeType) errors.push(t("Discharge type is required"));
    if (!form.drWrittenDischargeSummery || form.drWrittenDischargeSummery.replace(/<[^>]*>?/gm, '').trim() === "") errors.push(t("Detailed Clinical Summary is mandatory to proceed with discharge."));
    if (form.paymentClearance !== "Cleared") errors.push(t("Please check Payment Clearance. Status must be 'Cleared' to proceed."));

    if (errors.length > 0) {
      alert(errors.join("\n"));
      return;
    }

    onSubmit(form);
  };

  const handleExport = () => {
    setIsSubmitted(true);
    if (!form.drWrittenDischargeSummery || form.drWrittenDischargeSummery.replace(/<[^>]*>?/gm, '').trim() === "") {
      alert(t("Please Add Detailed Clinical Summary before exporting."));
      return;
    }
    exportDischargePDF(form, authCtx);
  };

  const handleOpenSummaryModal = () => {
    setTempSummary(form.drWrittenDischargeSummery || "");
    setShowSummaryModal(true);
    setIsPreviewMode(false);
  };

  const handleSaveSummary = () => {
    setForm(prev => ({ ...prev, drWrittenDischargeSummery: tempSummary }));
    setShowSummaryModal(false);
  };

  const handleVoiceInput = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Speech Recognition API is not supported in this browser. Please use Chrome.");
      return;
    }

    if (isListening) {
      // In a simple toggle, if already listening, we could stop it, 
      // but usually the onend from a single shot result will handle it.
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-IN'; // Indian English support
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      if (transcript) {
        setTempSummary(prev => {
          // If prev is empty or just <p><br></p>, start fresh
          const cleanPrev = prev.replace(/<[^>]*>?/gm, '').trim();
          if (cleanPrev === "") return `<p>${transcript}</p>`;

          // Append to existing HTML structure
          // Very basic append: find last </p> or just add a space and text
          if (prev.endsWith('</p>')) {
            return prev.slice(0, -4) + " " + transcript + "</p>";
          }
          return prev + " " + transcript;
        });
      }
    };

    recognition.onerror = (event) => {
      console.error(event.error);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.start();
  };

  const handleVoiceInputForNotes = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Speech Recognition API is not supported in this browser. Please use Chrome.");
      return;
    }

    if (isListeningNotes) return;

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-IN';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setIsListeningNotes(true);
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      if (transcript) {
        setForm(prev => ({
          ...prev,
          notes: prev.notes ? prev.notes + " " + transcript : transcript
        }));
      }
    };
    recognition.onerror = () => setIsListeningNotes(false);
    recognition.onend = () => setIsListeningNotes(false);
    recognition.start();
  };

  const pulseMicStyles = `
    @keyframes pulse-mic {
      0% { transform: scale(1); }
      50% { transform: scale(1.2); }
      100% { transform: scale(1); }
    }
  `;

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="xl"
      centered
      className={`${classes.patientModel} ${showSummaryModal ? classes.tintedModal : ""}`}
    >
      <style>{pulseMicStyles}</style>
      <Modal.Header closeButton className={classes.modalHeaderCustom}>
        <div className="d-flex justify-content-between align-items-center w-100 me-4">
          <Modal.Title>{t("Discharge Patient")} - {patient?.name}</Modal.Title>
          <Button
            size="sm"
            className={`d-flex align-items-center gap-1 fw-bold ${isSubmitted && !form.drWrittenDischargeSummery ? classes.highlightBtn : ""}`}
            onClick={handleOpenSummaryModal}
            style={{
              backgroundColor: '#f8f9fa', // Off-white / faint grey
              color: '#333',
              border: 'none',
              borderRadius: '6px',
              padding: '6px 14px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            <FaPlus /> {t("Add Summary")}
          </Button>
        </div>
      </Modal.Header>

      <Modal.Body className={classes.mbody}>
        <div className={classes.section}>
          <div className={classes.grid}>
            {/* Patient Info */}
            <div className={classes.formGroup}>
              <label>{t("Patient Name")}</label>
              <input
                type="text"
                name="patientName"
                value={form.patientName}
                onChange={handleChange}
                disabled
              />
            </div>

            <div className={classes.formGroup}>
              <label>{t("Age")}</label>
              <input
                type="number"
                name="age"
                value={form.age}
                onChange={handleChange}
                disabled
              />
            </div>

            <div className={classes.formGroup}>
              <label>{t("Sex")}</label>
              <select name="sex" value={form.sex} onChange={handleChange} disabled>
                <option value="">{t("Select")}</option>
                <option value="Male">{t("Male")}</option>
                <option value="Female">{t("Female")}</option>
                <option value="Other">{t("Other")}</option>
              </select>
            </div>

            <div className={classes.formGroup}>
              <label>{t("IPD No.")}</label>
              <input
                type="text"
                name="ipdNo"
                value={form.ipdNo}
                onChange={handleChange}
                disabled
              />
            </div>

            {/* Admission & Discharge */}
            <div className={classes.formGroup}>
              <label>{t("DOA")}</label>
              <input
                type="date"
                name="doa"
                value={form.doa}
                onChange={handleChange}
                disabled
              />
            </div>

            <div className={classes.formGroup}>
              <label>{t("Admission Time")}</label>
              <input
                type="time"
                name="doaTime"
                value={form.doaTime}
                onChange={handleChange}
                disabled
              />
            </div>

            <div className={classes.formGroup}>
              <label>{t("DOD")}</label>
              <input
                type="date"
                name="dod"
                value={form.dod}
                onChange={handleChange}
                disabled={patient?.status === "DISCHARGED"}
              />
            </div>

            <div className={classes.formGroup}>
              <label>{t("Discharge Time")}</label>
              <input
                type="time"
                name="dodTime"
                value={form.dodTime}
                onChange={handleChange}
                disabled={patient?.status === "DISCHARGED"}
              />
            </div>

            {/* Address and Diagnosis (Compact Row) */}
            <div className={classes.col2}>
              <label>{t("Address")}</label>
              <input
                type="text"
                name="address"
                value={form.address}
                onChange={handleChange}
                disabled
              />
            </div>

            <div className={classes.col2}>
              <label>{t("Diagnosis")}</label>
              <textarea
                name="diagnosis"
                rows="1"
                value={form.diagnosis}
                onChange={handleChange}
                disabled
                style={{ resize: 'none' }}
              />
            </div>

            {/* Row: Weight and other stats */}
            <div className={classes.formGroup}>
              <label>{t("Weight (kg)")}</label>
              <input
                type="number"
                name="weight"
                value={form.weight}
                onChange={handleChange}
                placeholder={t("kg")}
                disabled={patient?.status === "DISCHARGED"}
              />
            </div>

            <div className={classes.formGroup}>
              <label>{t("Discharge Type")}</label>
              <select
                name="dischargeType"
                value={form.dischargeType}
                onChange={handleChange}
                style={isSubmitted && !form.dischargeType ? { border: "2px solid #e53e3e", backgroundColor: "#fff5f5" } : {}}
                disabled={patient?.status === "DISCHARGED"}
              >
                <option value="">{t("Select Type")}</option>
                <option value="Normal">{t("Normal")}</option>
                <option value="AMA">{t("Against Medical Advice (AMA)")}</option>
                <option value="Referral">{t("Referral")}</option>
                <option value="Deceased">{t("Deceased")}</option>
              </select>
            </div>

            <div className={classes.formGroup}>
              <label>{t("Condition at Discharge")}</label>
              <input
                type="text"
                name="condition"
                value={form.condition}
                onChange={handleChange}
                placeholder={t("e.g., Stable")}
                disabled={patient?.status === "DISCHARGED"}
              />
            </div>

            <div className={classes.formGroup}>
              <label>{t("Payment Clearance")}</label>
              <select
                name="paymentClearance"
                value={form.paymentClearance}
                onChange={handleChange}
                style={form.paymentClearance !== "Cleared" ? { border: "2px solid #e53e3e", backgroundColor: "#fff5f5" } : {}}
                disabled={patient?.status === "DISCHARGED"}
              >
                <option value="">{t("Select")}</option>
                <option value="Cleared">{t("Cleared")}</option>
                <option value="Pending">{t("Pending")}</option>
              </select>
            </div>

            {/* Clinical History & Findings (New) - Side by Side Grid Layout - (col4 grid2) */}
            <div className={classes.grid2}>
              <div className={classes.formGroup}>
                <label className="fw-bold">{t("Personal History of")}</label>
                <textarea
                  name="pho"
                  rows="2"
                  value={form.pho}
                  onChange={handleChange}
                  placeholder={t("Enter Personal History...")}
                  disabled={patient?.status === "DISCHARGED"}
                  className="bg-white border rounded shadow-sm"
                />
              </div>

              <div className={classes.formGroup}>
                <label className="fw-bold">{t("Complaint of")}</label>
                <textarea
                  name="co"
                  rows="2"
                  value={form.co}
                  onChange={handleChange}
                  placeholder={t("Enter Complaints...")}
                  disabled={patient?.status === "DISCHARGED"}
                  className="bg-white border rounded shadow-sm"
                />
              </div>

              <div className={classes.formGroup}>
                <label className="fw-bold">{t("Known Case of")}</label>
                <textarea
                  name="kco"
                  rows="2"
                  value={form.kco}
                  onChange={handleChange}
                  placeholder={t("Enter Known Cases...")}
                  disabled={patient?.status === "DISCHARGED"}
                  className="bg-white border rounded shadow-sm"
                />
              </div>

              <div className={classes.formGroup}>
                <label className="fw-bold">{t("Clinical Findings")}</label>
                <textarea
                  name="cf"
                  rows="2"
                  value={form.cf}
                  onChange={handleChange}
                  placeholder={t("Enter Clinical Findings...")}
                  disabled={patient?.status === "DISCHARGED"}
                  className="bg-white border rounded shadow-sm"
                />
              </div>
            </div>

            {/* Row: Next Appointment and Notes/Remarks */}
            <div className={classes.formGroup}>
              <label>{t("Next Appointment (if any)")}</label>
              <input
                type="date"
                name="nextAppointment"
                value={form.nextAppointment}
                onChange={handleChange}
                disabled={patient?.status === "DISCHARGED"}
              />
            </div>

            <div className={classes.col3}>
              <label className="d-flex align-items-center gap-2">
                {t("Notes / Remarks")}
                {patient?.status !== "DISCHARGED" && (
                  <FaMicrophone
                    onClick={handleVoiceInputForNotes}
                    style={{
                      cursor: "pointer",
                      color: isListeningNotes ? "#f44336" : "#25d366",
                      animation: isListeningNotes ? "pulse-mic 1s infinite" : "none"
                    }}
                    title={t("Voice Input")}
                  />
                )}
              </label>
              <textarea
                name="notes"
                rows="2"
                value={form.notes}
                onChange={handleChange}
                placeholder={t("Enter additional notes or remarks...")}
                disabled={patient?.status === "DISCHARGED"}
              />
            </div>
          </div>
        </div>

        {/* --- Secondary Modal for Rich Text Editor --- */}
        <Modal
          show={showSummaryModal}
          onHide={() => setShowSummaryModal(false)}
          size="lg"
          centered
          className={`${classes.secondaryModal} rich-text-modal text-start`}
          backdrop="static" // Ensure it doesn't close on background click to distinguish from main modal
        >
          <Modal.Header closeButton className="modal-header-themed">
            <Modal.Title>{t("Detailed Discharge Summary")}</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ minHeight: '450px' }}>
            <div className="mb-3 d-flex justify-content-between align-items-center">
              <span className="text-muted small">{t("Write comprehensive clinical notes, treatment given, and advice.")}</span>
              <Button
                size="sm"
                className="btn-premium"
                onClick={() => setIsPreviewMode(!isPreviewMode)}
              >
                {isPreviewMode ? <>{t("Edit Mode")}</> : <>{t("Preview Mode")}</>}
              </Button>
            </div>

            {!isPreviewMode ? (
              <div className="cert-editor-wrapper">
                <ReactQuill
                  value={tempSummary}
                  onChange={setTempSummary}
                  theme="snow"
                  placeholder={t("Start typing the detailed discharge summary here...")}
                  readOnly={patient?.status === "DISCHARGED"}
                />
              </div>
            ) : (
              <div
                className="p-3 border rounded bg-white shadow-sm"
                style={{ minHeight: '340px', overflowY: 'auto' }}
              >
                <div dangerouslySetInnerHTML={{ __html: tempSummary || `<p class='text-muted'>${t("No content to preview.")}</p>` }} />
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowSummaryModal(false)}>
              {patient?.status === "DISCHARGED" ? t("Close") : t("Discard")}
            </Button>
            {patient?.status !== "DISCHARGED" && (
              <>
                <Button className="btn-premium" onClick={handleSaveSummary}>
                  <FaCheck className="me-1" /> {t("Save Summary")}
                </Button>
                <Button
                  className={`${isListening ? classes.voiceMicActive : classes.voiceMicBtn}`}
                  onClick={handleVoiceInput}
                  title={isListening ? t("Listening...") : t("Click to speak")}
                >
                  <FaMicrophone className={isListening ? classes.micPulse : ""} />
                </Button>
              </>
            )}
          </Modal.Footer>
        </Modal>
      </Modal.Body >

      <Modal.Footer className={classes.actions}>
        {/* ✅ Print Section */}
        <div onClick={(e) => {
          if (!form.drWrittenDischargeSummery || form.drWrittenDischargeSummery.replace(/<[^>]*>?/gm, '').trim() === "") {
            e.stopPropagation();
            setIsSubmitted(true);
            alert(t("Please Add Detailed Clinical Summary before printing."));
          }
        }} style={{ display: 'inline-block' }}>
          <Print formData={form} authCtx={authCtx} paperSize="a4" />
        </div>

        <Button
          onClick={handleExport}
          className="btn-export-premium"
        >
          <FaFileExport className="me-2" /> {t("Export")}
        </Button>

        {/* ✅ Main Actions */}
        <Button onClick={handleSubmit} className="btn-premium" disabled={loading || patient?.status === 'DISCHARGED'}>
          {loading ? (
            <Spinner animation="border" size="sm" className="me-2" />
          ) : (
            <FaSignOutAlt className="me-2" />
          )}
          {loading ? t("Discharging...") : patient?.status === 'DISCHARGED' ? t("Discharged") : t("Discharge")}
        </Button>
        <Button onClick={onClose} className="btn-secondary-premium">
          <FaTimes className="me-2" /> {t("Cancel")}
        </Button>
      </Modal.Footer>
    </Modal >
  );
};

export default DischargeModal;
